import type { ConversationMessage } from '@care-companion/client';
import type { CareCompanionReference } from '@care-companion/protocol';
import { Tooltip } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';

interface CareCompanionAssistantMessageProps {
	message: ConversationMessage;
	citationNumbers: Map<string, number>;
}

export function CareCompanionAssistantMessage({ message, citationNumbers }: CareCompanionAssistantMessageProps): JSX.Element {
	const { citations, markdown } = createCitationMarkdown(message, citationNumbers);

	return (
		<MarkdownContent>
			<ReactMarkdown
				remarkPlugins={[remarkGfm]}
				components={{
					a: ({ href, ...props }) => {
						const citation = href == null ? undefined : citations.get(href);
						if (citation != null) {
							return <ReferenceCitation number={citation.number} reference={citation.reference} />;
						}

						return <a href={href} {...props} />;
					},
				}}
			>
				{markdown}
			</ReactMarkdown>
		</MarkdownContent>
	);
}

export function getCitationNumbers(messages: ConversationMessage[]): Map<string, number> {
	const numbers = new Map<string, number>();

	for (const message of messages) {
		if (message.role !== 'assistant') continue;

		for (const part of message.parts ?? []) {
			if (part.type !== 'reference') continue;

			const key = getReferenceKey(part.reference);
			if (!numbers.has(key)) numbers.set(key, numbers.size + 1);
		}
	}

	return numbers;
}

function ReferenceCitation({ number, reference }: { number: number; reference: CareCompanionReference }): JSX.Element {
	const title = reference.title ?? reference.uri;

	return (
		<Tooltip
			content={
				<CitationTooltipContent>
					<CitationTitle>{title}</CitationTitle>
					<CitationExcerpt>
						<ReactMarkdown remarkPlugins={[remarkGfm]}>{reference.content}</ReactMarkdown>
					</CitationExcerpt>
					{isSafeReferenceUri(reference.uri) ? (
						<a href={reference.uri} target='_blank' rel='noreferrer'>
							{reference.uri}
						</a>
					) : (
						<CitationUri>{reference.uri}</CitationUri>
					)}
				</CitationTooltipContent>
			}
			variant='light'
		>
			<CitationButton type='button' aria-label={`Reference ${number}: ${title}`}>
				[{number}]
			</CitationButton>
		</Tooltip>
	);
}

function createCitationMarkdown(
	message: ConversationMessage,
	citationNumbers: Map<string, number>,
): { citations: Map<string, { number: number; reference: CareCompanionReference }>; markdown: string } {
	const parts = message.parts ?? [];
	const hasTextParts = parts.some((part) => part.type === 'text');
	const citations = new Map<string, { number: number; reference: CareCompanionReference }>();
	let markdown = hasTextParts ? '' : message.content;
	let citationIndex = 0;

	for (const part of parts) {
		if (part.type === 'text') {
			markdown += part.text;
			continue;
		}

		const citationUrl = `https://care-companion-reference.invalid/${citationIndex}`;
		const number = citationNumbers.get(getReferenceKey(part.reference));
		if (number == null) continue;

		citations.set(citationUrl, { number, reference: part.reference });
		markdown += ` [${number}](${citationUrl})`;
		citationIndex += 1;
	}

	return { citations, markdown };
}

function getReferenceKey(reference: CareCompanionReference): string {
	return JSON.stringify([reference.title, reference.uri, reference.content]);
}

function isSafeReferenceUri(value: string): boolean {
	try {
		const url = new URL(value);
		return url.protocol === 'http:' || url.protocol === 'https:';
	} catch {
		return false;
	}
}

const MarkdownContent = styled.div`
	p,
	ul,
	ol,
	pre,
	blockquote,
	table {
		margin: 0 0 ${tokens.spacing.x1_5};
	}

	& > :last-child {
		margin-bottom: 0;
	}

	ul,
	ol {
		padding-inline-start: ${tokens.spacing.x3};
	}

	li + li {
		margin-top: ${tokens.spacing.x0_5};
	}

	a {
		color: ${tokens.color.content.interactive};
		text-decoration: underline;
	}

	blockquote {
		padding-inline-start: ${tokens.spacing.x1_5};
		border-inline-start: 3px solid ${tokens.color.border.muted};
		color: ${tokens.color.content.muted};
	}

	code {
		padding: 0 ${tokens.spacing.x0_5};
		background: ${tokens.color.bg.surfaceMuted};
		border-radius: ${tokens.borderRadius.small};
		font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
		font-size: 0.9em;
	}

	pre {
		overflow-x: auto;
		padding: ${tokens.spacing.x1_5};
		background: ${tokens.color.bg.surfaceMuted};
		border-radius: ${tokens.borderRadius.medium};

		code {
			padding: 0;
			background: transparent;
		}
	}

	table {
		width: 100%;
		border-collapse: collapse;
	}

	th,
	td {
		padding: ${tokens.spacing.x1};
		border: 1px solid ${tokens.color.border.muted};
		text-align: start;
	}
`;

const CitationButton = styled.button`
	display: inline;
	margin: 0;
	padding: 0;
	border: 0;
	color: ${tokens.color.content.interactive};
	background: transparent;
	font: inherit;
	font-size: 0.8em;
	font-weight: 700;
	vertical-align: super;
	text-decoration: underline;
	cursor: pointer;
`;

const CitationTooltipContent = styled.div`
	display: grid;
	gap: ${tokens.spacing.x0_25};
	max-height: 220px;
	overflow-y: auto;
	line-height: 1.35;

	& > a {
		color: ${tokens.color.content.interactive};
		font: ${tokens.text.label.xSmallRegular};
		line-height: 1.25;
		word-break: break-all;
	}
`;

const CitationTitle = styled.strong`
	font: ${tokens.text.label.smallSemiBold};
	line-height: 1.25;
	overflow-wrap: anywhere;
`;

const CitationExcerpt = styled.div`
	font: inherit;
	line-height: inherit;

	h1,
	h2,
	h3,
	h4,
	h5,
	h6 {
		margin: 0;
		font: inherit;
		font-weight: 600;
	}

	p,
	ul,
	ol,
	blockquote,
	pre {
		margin: 0;
	}

	p + p,
	ul,
	ol,
	blockquote,
	pre {
		margin-top: ${tokens.spacing.x0_25};
	}

	ul,
	ol {
		padding-inline-start: ${tokens.spacing.x1_5};
	}

	blockquote {
		padding-inline-start: ${tokens.spacing.x0_5};
		border-inline-start: 2px solid ${tokens.color.border.muted};
	}

	code {
		font-size: 0.9em;
	}

	pre {
		overflow-x: auto;
		white-space: pre-wrap;
	}

	a {
		color: ${tokens.color.content.interactive};
		text-decoration: underline;
	}
`;

const CitationUri = styled.span`
	font: ${tokens.text.label.xSmallRegular};
	line-height: 1.25;
	word-break: break-all;
`;
